import { Box, useRecipe } from '@chakra-ui/react';
import { useRouter } from 'next/router';
import React from 'react';

import useIsMobile from 'lib/hooks/useIsMobile';
import useIsMounted from 'lib/hooks/useIsMounted';
import getQueryParamString from 'lib/router/getQueryParamString';
import type { SelectOption } from 'toolkit/chakra/select';
import { recipe as cardRecipe } from 'toolkit/theme/recipes/card.recipe';
import ActionBar, { ACTION_BAR_HEIGHT_DESKTOP } from 'ui/shared/ActionBar';
import { TabsSelect } from 'ui/shared/filters/TabsSelect';
import Pagination from 'ui/shared/pagination/Pagination';
import TxsWithAPISorting from 'ui/txs/TxsWithAPISorting';

import AddressCsvExportLink from './AddressCsvExportLink';
import useAddressTxsQuery from './useAddressTxsQuery';

type Props = {
  shouldRender?: boolean;
  isQueryEnabled?: boolean;
};

const AddressTxs = ({ shouldRender = true, isQueryEnabled = true }: Props) => {
  const router = useRouter();
  const isMounted = useIsMounted();
  const isMobile = useIsMobile();
  const currentAddress = getQueryParamString(router.query.hash);

  const { query, filterValue, onFilterChange, sort, setSort } = useAddressTxsQuery({
    addressHash: currentAddress,
    enabled: isQueryEnabled,
  });

  const options: Array<SelectOption> = [
    {
      value: 'all',
      label: 'All',
    },
    {
      value: 'from',
      label: 'Outgoing',
    },
    {
      value: 'to',
      label: 'Incoming',
    },
  ];

  const handleTabChange = React.useMemo(() => (e: { value: string }) => {
    onFilterChange(e.value);
  }, [ onFilterChange ]);

  const recipe = useRecipe({ recipe: cardRecipe });

  if (!isMounted || !shouldRender) {
    return null;
  }

  const filter = (
    <TabsSelect
      onChange={ handleTabChange }
      collection={ options }
      initialValue={ options[0].value }
    />
  );

  const csvExportLink = (
    <AddressCsvExportLink
      address={ currentAddress }
      params={{ type: 'transactions', filterType: 'address', filterValue }}
      ml="auto"
      isLoading={ query.pagination.isLoading }
    />
  );

  return (
    <>
      <Box css={ recipe() } mt={ 6 }>
        { !isMobile && (
          <ActionBar borderTopRadius="base">
            { filter }
            { currentAddress && csvExportLink }
          </ActionBar>
        ) }
        <TxsWithAPISorting
          filter={ filter }
          filterValue={ filterValue }
          query={ query }
          currentAddress={ typeof currentAddress === 'string' ? currentAddress : undefined }
          enableTimeIncrement
          socketType="address_txs"
          top={ ACTION_BAR_HEIGHT_DESKTOP }
          sorting={ sort }
          setSort={ setSort }
        />
      </Box>
      <Pagination { ...query.pagination }/>
    </>
  );
};

export default AddressTxs;
